import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import { t } from 'i18next';

import MedicationIcon from '@mui/icons-material/Medication';
import OpacityIcon from '@mui/icons-material/Opacity';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useMedications } from '../../../hooks/useMedications';

import MedicationAppearanceIcon from './edicationAppearanceIcon';
import { ReactComponent as PillIcon } from 'assets/images/icons/By CAC/Icones_cac_07_cinza.svg';
import { ReactComponent as SyrupIcon } from 'assets/images/icons/By CAC/Icones_cac_14_cinza.svg';
import { ReactComponent as CreamIcon } from 'assets/images/icons/By CAC/Icones_cac_15_cinza.svg';
import { ReactComponent as SprayIcon } from 'assets/images/icons/By CAC/Icones_cac_16_cinza.svg';
import { ReactComponent as SachetIcon } from 'assets/images/icons/By CAC/Icones_cac_17_cinza.svg';
import { ReactComponent as CapsuleIcon } from 'assets/images/icons/By CAC/Icones_cac_18_cinza.svg';
import { ReactComponent as InjectionIcon } from 'assets/images/icons/By CAC/Icones_cac_19_cinza.svg';

const AddMedication = ({ patientUUID }) => {
    const [isAddingMedication, setIsAddingMedication] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [medicationToDelete, setMedicationToDelete] = useState(null);

    const MEDICATION_APPEARANCES = [
        { value: 'PILL', label: t('medication.appearanceTypes.PILL') },
        { value: 'CAPSULE', label: t('medication.appearanceTypes.CAPSULE') },
        { value: 'SACHET', label: t('medication.appearanceTypes.SACHET') },
        { value: 'DROPS', label: t('medication.appearanceTypes.DROPS') },
        { value: 'SYRUP', label: t('medication.appearanceTypes.SYRUP') },
        { value: 'INJECTION', label: t('medication.appearanceTypes.INJECTION') },
        { value: 'CREAM', label: t('medication.appearanceTypes.CREAM') },
        { value: 'SPRAY', label: t('medication.appearanceTypes.SPRAY') },
        { value: 'OTHER', label: t('medication.appearanceTypes.OTHER') },
        { value: 'UNKNOWN', label: t('medication.appearanceTypes.UNKNOWN') }
    ];

    const MEDICATION_TAKING_MOMENT = [
        { value: 'AFTER_WAKEUP', label: t('medication.takingMoment.AFTER_WAKEUP') },
        { value: 'BEFORE_BKFST', label: t('medication.takingMoment.BEFORE_BKFST') },
        { value: 'BKFST', label: t('medication.takingMoment.BKFST') },
        { value: 'AFTER_BKFST', label: t('medication.takingMoment.AFTER_BKFST') },
        { value: 'BEFORE_LUNCH', label: t('medication.takingMoment.BEFORE_LUNCH') },
        { value: 'LUNCH', label: t('medication.takingMoment.LUNCH') },
        { value: 'AFTER_LUNCH', label: t('medication.takingMoment.AFTER_LUNCH') },
        { value: 'BEFORE_DINNER', label: t('medication.takingMoment.BEFORE_DINNER') },
        { value: 'DINNER', label: t('medication.takingMoment.DINNER') },
        { value: 'AFTER_DINNER', label: t('medication.takingMoment.AFTER_DINNER') },
        { value: 'BEFORE_BED', label: t('medication.takingMoment.BEFORE_BED') },
        { value: 'HOURLY', label: t('medication.takingMoment.HOURLY') },
        { value: 'SOS', label: t('medication.takingMoment.SOS') },
        { value: 'UNKNOWN', label: t('medication.takingMoment.UNKNOWN') }
    ];

    const APPEARANCE_ICON_MAP = {
        PILL: PillIcon,
        CAPSULE: CapsuleIcon,
        SACHET: SachetIcon,
        DROPS: OpacityIcon,
        SYRUP: SyrupIcon,
        INJECTION: InjectionIcon,
        CREAM: CreamIcon,
        SPRAY: SprayIcon,
        OTHER: MedicationIcon,
        UNKNOWN: HelpOutlineIcon
    };

    const formatDosage = (dosage) => {
        if (!Array.isArray(dosage) || dosage.length === 0) return '-';

        return dosage
            .map(
                ([period, dose]) => `${dose} (${t(`medication.takingMoment.${period}`)})`
            )
            .join(', ');
    };

    const [form, setForm] = useState({
        name: '',
        appearance: '',
        dosage: [{ period: '', dose: '' }],
        duration: '',
        indefiniteDuration: false,
        notes: ''
    });

    const {
        medications,
        loading,
        loadPatientMedications,
        addMedication,
        archiveMedication
    } = useMedications();

    useEffect(() => {
        if (patientUUID) {
            loadPatientMedications(patientUUID, {
                onlyStarted: false,
                includeArchived: false
            });
        }
    }, [patientUUID, loadPatientMedications]);

    const handleChange = (field) => (event) => {
        setForm((prev) => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleDosageChange = (index, field) => (event) => {
        const value = event.target.value;

        setForm((prev) => {
            const updated = [...prev.dosage];
            updated[index] = { ...updated[index], [field]: value };
            return { ...prev, dosage: updated };
        });
    };

    const addDosageRow = () => {
        setForm((prev) => ({
            ...prev,
            dosage: [...prev.dosage, { period: '', dose: '' }]
        }));
    };

    const removeDosageRow = (index) => {
        setForm((prev) => ({
            ...prev,
            dosage: prev.dosage.filter((_, i) => i !== index)
        }));
    };

    const handleIndefiniteDurationChange = (event) => {
        const checked = event.target.checked;

        setForm((prev) => ({
            ...prev,
            indefiniteDuration: checked,
            duration: checked ? -1 : ''
        }));
    };

    const handleCancel = () => {
        setIsAddingMedication(false);
        setForm({
            name: '',
            appearance: '',
            dosage: {
                dose: '',
                period: ''
            },
            duration: '',
            indefiniteDuration: false,
            notes: ''
        });
    };

    const handleSubmit = async () => {
        const payload = {
            patientUUID,
            name: form.name,
            appearance: form.appearance,
            dosage: form.dosage.map((d) => [d.period, d.dose]),
            duration: form.indefiniteDuration ? -1 : Number(form.duration),
            notes: form.notes
        };

        await addMedication(payload);

        await loadPatientMedications(patientUUID, {
            onlyStarted: false,
            includeArchived: false
        });

        handleCancel();
    };

    const openDeleteDialog = (medication) => {
        setMedicationToDelete(medication);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setMedicationToDelete(null);
    };

    const confirmDeleteMedication = async () => {
        if (!medicationToDelete) return;

        await archiveMedication(medicationToDelete.uuid);

        await loadPatientMedications(patientUUID, {
            onlyStarted: false,
            includeArchived: false
        });

        closeDeleteDialog();
    };

    return (
        <Box mt={2}>
            <Card>
                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" mb={2}>
                        <Typography variant="h5" fontWeight={700}>
                            {t('medication.title')}
                        </Typography>

                        {!isAddingMedication && (
                            <Button
                                variant="outlined"
                                onClick={() => setIsAddingMedication(true)}
                            >
                                {t('medication.add')}
                            </Button>
                        )}
                    </Box>

                    {/* Add Medication Form */}
                    {isAddingMedication && (
                        <Card variant="outlined">
                            <CardContent>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <TextField
                                        label={t('medication.name')}
                                        value={form.name}
                                        onChange={handleChange('name')}
                                        fullWidth
                                    />

                                    <TextField
                                        select
                                        label={t('medication.appearance')}
                                        value={form.appearance}
                                        onChange={handleChange('appearance')}
                                        fullWidth
                                    >
                                        {MEDICATION_APPEARANCES.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <Box display="flex" flexDirection="column" gap={2}>
                                        {form.dosage.map((d, index) => (
                                            <Box
                                                key={index}
                                                display="flex"
                                                gap={2}
                                                alignItems="center"
                                            >
                                                <TextField
                                                    label={t('medication.dose')}
                                                    value={d.dose}
                                                    onChange={handleDosageChange(
                                                        index,
                                                        'dose'
                                                    )}
                                                    fullWidth
                                                />

                                                <TextField
                                                    select
                                                    label={t('medication.frequency')}
                                                    value={d.period}
                                                    onChange={handleDosageChange(
                                                        index,
                                                        'period'
                                                    )}
                                                    fullWidth
                                                >
                                                    {MEDICATION_TAKING_MOMENT.map(
                                                        (opt) => (
                                                            <MenuItem
                                                                key={opt.value}
                                                                value={opt.value}
                                                            >
                                                                {opt.label}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </TextField>

                                                {/* Remove */}
                                                {form.dosage.length > 1 && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            removeDosageRow(index)
                                                        }
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        ))}

                                        {/* Add */}
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={addDosageRow}
                                            sx={{ alignSelf: 'flex-start' }}
                                        >
                                            {t('medication.addDosage')}
                                        </Button>
                                    </Box>

                                    <Box display="flex" alignItems="center" gap={2}>
                                        <TextField
                                            label={t('medication.duration')}
                                            type="number"
                                            value={
                                                form.indefiniteDuration
                                                    ? ''
                                                    : form.duration
                                            }
                                            onChange={handleChange('duration')}
                                            disabled={form.indefiniteDuration}
                                            inputProps={{ min: 1 }}
                                            fullWidth
                                        />

                                        <Box display="flex" alignItems="center">
                                            <input
                                                type="checkbox"
                                                checked={form.indefiniteDuration}
                                                onChange={handleIndefiniteDurationChange}
                                                style={{ marginRight: 8 }}
                                            />
                                            <Typography variant="body2">
                                                {t('medication.indefinite')}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <TextField
                                        label={t('medication.notes')}
                                        value={form.notes}
                                        onChange={handleChange('notes')}
                                        multiline
                                        rows={3}
                                        fullWidth
                                    />

                                    <Box display="flex" justifyContent="flex-end" gap={2}>
                                        <Button variant="outlined" onClick={handleCancel}>
                                            {t('common.cancel')}
                                        </Button>

                                        <Button
                                            variant="contained"
                                            onClick={handleSubmit}
                                            disabled={loading}
                                        >
                                            {t('common.add')}
                                        </Button>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Patient Medication */}
                    {!isAddingMedication && (
                        <>
                            {loading && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('common.loading')}
                                </Typography>
                            )}

                            {!loading && medications.length === 0 && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        {t('medication.noMedication')}
                                    </Typography>
                                </Box>
                            )}

                            {!loading && medications.length > 0 && (
                                <Box
                                    display="grid"
                                    gridTemplateColumns="repeat(auto-fill, minmax(500px, 1fr))"
                                    gap={2}
                                >
                                    {medications.map((med) => {
                                        const Icon =
                                            APPEARANCE_ICON_MAP[med.appearance] ||
                                            HelpOutlineIcon;

                                        return (
                                            <Card
                                                key={med.uuid}
                                                variant="outlined"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'stretch',
                                                    borderRadius: 2,
                                                    minHeight: 140
                                                }}
                                            >
                                                {/* Icon */}
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{
                                                        width: 72,
                                                        backgroundColor: '#f0f4f8'
                                                    }}
                                                >
                                                    <MedicationAppearanceIcon
                                                        Icon={Icon}
                                                        size={54}
                                                        color="#1976d2"
                                                    />
                                                </Box>

                                                {/* Content */}
                                                <CardContent
                                                    sx={{ flex: 1, position: 'relative' }}
                                                >
                                                    {/* Header */}
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight={700}
                                                        >
                                                            {med.name}
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            color={
                                                                med.started
                                                                    ? 'primary.main'
                                                                    : 'error.main'
                                                            }
                                                            fontWeight={600}
                                                        >
                                                            {med.started
                                                                ? t('medication.started')
                                                                : t(
                                                                      'medication.notStarted'
                                                                  )}
                                                        </Typography>
                                                    </Box>

                                                    {/* Dosage */}
                                                    <Box mt={1}>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {formatDosage(med.dosage)}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {med.duration === -1
                                                                ? t(
                                                                      'medication.indefinite2'
                                                                  )
                                                                : `${med.duration} ${t('medication.days')}`}
                                                        </Typography>
                                                    </Box>

                                                    {/* Notes */}
                                                    {med.notes && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            mt={1}
                                                            pr={4}
                                                        >
                                                            {med.notes}
                                                        </Typography>
                                                    )}

                                                    {/* Delete */}
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() =>
                                                            openDeleteDialog(med)
                                                        }
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: 8,
                                                            right: 8
                                                        }}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
            <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
                <DialogTitle>{t('medication.deleteTitle')}</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {t('medication.deleteConfirmation', {
                            name: medicationToDelete?.name
                        })}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeDeleteDialog}>{t('common.cancel')}</Button>

                    <Button
                        onClick={confirmDeleteMedication}
                        color="error"
                        variant="contained"
                    >
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AddMedication;
